import { config } from './config';
import { restoreSession } from './auth';

export type Rendition = { path: string; bytes: number; sha256: string };

/** 与后端 src/lib/paths.mjs 的 XHE_KBPS_OPTIONS 一致 */
export const BITRATE_OPTIONS = [64, 96, 128] as const;
export const DEFAULT_BITRATE = 64;

export type Album = {
  albumId: string;
  name: string;
  status: 'draft' | 'published';
  sortOrder: number;
  coverKey: string | null;
  coverVersion: number;
  /** 这张专辑新转码时用的主用档码率 */
  audioBitrateKbps?: number;
  /** 勾上后这张专辑会出现在官网的试听区，只对已发布的专辑生效 */
  previewOnSite?: boolean;
};

export type TrackStatus = 'uploading' | 'transcoding' | 'ready' | 'failed';

export type Track = {
  trackId: string;
  albumId: string;
  name: string;
  artist: string;
  sortOrder: number;
  status: TrackStatus;
  version: number;
  durationMs?: number;
  bundled?: boolean;
  errorMessage?: string;
  /** 实际转出来的主用档码率，可能和所属专辑当前的设置不一样 */
  bitrateKbps?: number;
  /** 档位名带真实码率（xhe64 / xhe96 / …），所以只能按前缀取 */
  renditions?: Record<string, Rendition>;
};

const pick = (renditions: Track['renditions'], prefix: string) => {
  const name = Object.keys(renditions ?? {}).find((key) => key.startsWith(prefix));
  return name ? renditions![name] : undefined;
};

export const primaryRendition = (track: Track) => pick(track.renditions, 'xhe');

/** 主用档的实际码率，优先用落库的值，老曲目没有这个字段就从档位名里读 */
export function trackBitrate(track: Track): number | undefined {
  if (track.bitrateKbps) return track.bitrateKbps;
  const name = Object.keys(track.renditions ?? {}).find((key) => key.startsWith('xhe'));
  const kbps = name ? Number(name.slice(3)) : NaN;
  return Number.isFinite(kbps) ? kbps : undefined;
}

export type Job = {
  jobId: string;
  trackId: string;
  trackName: string;
  state: string;
  createdAt: string;
  errorMessage?: string;
};

export type CatalogVersion = {
  version: number;
  publishedAt: string;
  actor: string;
  note: string;
  albumCount: number;
  trackCount: number;
  rolledBackFrom?: number;
  diff: { added: string[]; removed: string[]; changed: string[] };
};

export type PublishResult = {
  version: number;
  albumCount: number;
  trackCount: number;
  invalidationId: string | null;
  diff: { added: string[]; removed: string[]; changed: string[] };
};

export type Grant = {
  baseUrl: string;
  expiresAt: number;
  query: Record<string, string>;
};

export type NewsState = 'draft' | 'published';

export type NewsPost = {
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  tag: string | null;
  state: NewsState;
  publishedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
};

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const session = await restoreSession();
  if (!session) throw new ApiError(401, 'Your session expired. Please sign in again.');

  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(res.status, payload.message ?? `Request failed (${res.status})`);
  return payload as T;
}

export const api = {
  listAlbums: () => request<{ albums: Album[] }>('GET', '/admin/albums'),
  createAlbum: (name: string) => request<{ album: Album }>('POST', '/admin/albums', { name }),
  updateAlbum: (
    albumId: string,
    patch: {
      name?: string; status?: string; audioBitrateKbps?: number; previewOnSite?: boolean;
    },
  ) => request<{ album: Album }>('PATCH', `/admin/albums/${albumId}`, patch),
  deleteAlbum: (albumId: string) => request<void>('DELETE', `/admin/albums/${albumId}`),
  reorderAlbums: (order: string[]) => request<void>('POST', '/admin/albums/reorder', { order }),
  requestCoverUpload: (albumId: string, filename: string, contentType: string) =>
    request<{ uploadUrl: string; coverPath: string }>(
      'POST', `/admin/albums/${albumId}/cover`, { filename, contentType }),

  listTracks: (albumId: string) =>
    request<{ tracks: Track[] }>('GET', `/admin/tracks?albumId=${encodeURIComponent(albumId)}`),
  requestUpload: (albumId: string, filename: string, contentType: string) =>
    request<{ track: Track; uploadUrl: string }>(
      'POST', '/admin/uploads', { albumId, filename, contentType }),
  updateTrack: (trackId: string, patch: Partial<Pick<Track, 'name' | 'artist' | 'bundled'>>) =>
    request<{ track: Track }>('PATCH', `/admin/tracks/${trackId}`, patch),
  deleteTrack: (trackId: string) => request<void>('DELETE', `/admin/tracks/${trackId}`),
  reorderTracks: (order: string[]) => request<void>('POST', '/admin/tracks/reorder', { order }),
  /** 不传码率时后端沿用这首上次的档，再退到专辑设置 */
  transcode: (trackId: string, bitrateKbps?: number) =>
    request<{ job: Job }>('POST', `/admin/tracks/${trackId}/transcode`,
      bitrateKbps ? { bitrateKbps } : undefined),
  preview: (trackId: string) =>
    request<{ url: string; rendition: Rendition }>('GET', `/admin/tracks/${trackId}/preview`),

  listJobs: () => request<{ jobs: Job[] }>('GET', '/admin/jobs'),
  previewPublish: () => request<{
    diff: PublishResult['diff'];
    albumCount: number;
    trackCount: number;
    currentVersion: number;
  }>('GET', '/admin/publish/preview'),
  publish: (note: string) => request<PublishResult>('POST', '/admin/publish', { note }),
  rollback: (toVersion: number) =>
    request<PublishResult & { rolledBackFrom: number }>('POST', '/admin/rollback', { toVersion }),
  listVersions: () => request<{ versions: CatalogVersion[] }>('GET', '/admin/versions'),
  grant: () => request<Grant>('GET', '/admin/grant'),

  listNews: () => request<{ posts: NewsPost[] }>('GET', '/admin/news'),
  createNews: (post: Partial<NewsPost>) =>
    request<{ post: NewsPost }>('POST', '/admin/news', post),
  /** slug 是主键，改了等于换地址，后端会搬记录再删旧的 */
  updateNews: (slug: string, post: Partial<NewsPost>) =>
    request<{ post: NewsPost }>('PATCH', `/admin/news/${encodeURIComponent(slug)}`, post),
  deleteNews: (slug: string) =>
    request<void>('DELETE', `/admin/news/${encodeURIComponent(slug)}`),
};

/**
 * 直传 S3，用 XMLHttpRequest 而不是 fetch，因为只有它能报上传进度。
 *
 * 收 Blob 而不是 File：封面走染色处理后拿到的是 canvas 导出的 Blob。
 * contentType 必须和预签名时声明的一致，否则 S3 会判签名不符，所以显式传进来。
 */
export function uploadFile(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Upload was interrupted'));
    xhr.send(body);
  });
}

export function signedUrl(grant: Grant | null, relativePath: string) {
  if (!grant) return '';
  return `${grant.baseUrl}${relativePath}?${new URLSearchParams(grant.query)}`;
}
